import useBootstrapData from '@/hooks/useBootstrapData'
import analytics from '@/shared-utils/analytics'
import { type MouseEvent, type ReactNode, useEffect, useRef } from 'react'
import { type ComponentId, type LogComponentLoadParams, type LogComponentTapParams } from './types'

const INTERACTIVE_SELECTOR =
  'button, a, input, select, [role="button"], [role="tab"], [role="menuitem"], [data-track="tap"]'

function logComponentLoad({ componentId, loadTimeMs, isMobile, cguid, pageName }: LogComponentLoadParams) {
  try {
    analytics.log({
      message: 'homepage_component_loaded',
      componentId,
      loadTimeMs,
      isMobile,
      cguid,
      pageName,
    })
  } catch {
    /* metrics must never crash the UI */
  }
}

function logComponentTap({ cguid, componentId, isMobile, pageName, target }: LogComponentTapParams) {
  try {
    analytics.log({ message: 'homepage_component_tap', componentId, target, isMobile, cguid, pageName })
  } catch {
    /* metrics must never crash the UI */
  }
}

function getTargetLabel(el: Element): string {
  const tag = el.tagName.toLowerCase()
  const role = el.getAttribute('role')
  const roleSegment = role ? `[role=${role}]` : ''
  const label = el.getAttribute('aria-label') || (el as HTMLElement).dataset?.track || ''
  return `${tag}${roleSegment}:${label.trim().slice(0, 40)}`
}

export default function ComponentMetricsBoundary({
  children,
  componentId,
  isReady = true,
}: Readonly<{
  children: ReactNode
  componentId: ComponentId
  isReady?: boolean
}>) {
  const {
    isMobile,
    key: pageName,
    webstats: { clientGUID: cguid },
  } = useBootstrapData()
  // Intentionally never resets — each boundary instance has a static componentId.
  // To track a different component, mount a separate boundary
  const hasFiredLoad = useRef(false)

  useEffect(() => {
    if (isReady && !hasFiredLoad.current) {
      hasFiredLoad.current = true
      logComponentLoad({ componentId, loadTimeMs: Math.round(performance.now()), isMobile, cguid, pageName })
    }
  }, [isReady, componentId, isMobile, cguid, pageName])

  function handleClick(e: MouseEvent) {
    const target = (e.target as Element).closest?.(INTERACTIVE_SELECTOR)
    if (target) {
      logComponentTap({ componentId, target: getTargetLabel(target), isMobile, cguid, pageName })
    }
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div onClick={handleClick} style={{ display: 'contents' }}>
      {children}
    </div>
  )
}
